import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { takeReturn } from "@/lib/returnTo";
import { toast } from "sonner";
import { KeyRound, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const DANCER = "/studiov-dancer.png";

const Sparkle = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="currentColor" aria-hidden>
    <path d="M50 0 C54 30 70 46 100 50 C70 54 54 70 50 100 C46 70 30 54 0 50 C30 46 46 30 50 0 Z" />
  </svg>
);

const Wordmark = ({ className = "" }) => (
  <div className={`flex items-baseline ${className}`}>
    <span className="font-head font-extrabold tracking-tight text-white">Studio</span>
    <span className="font-head font-extrabold text-teal leading-none -ml-1 -mb-1" style={{ fontSize: "1.35em" }}>V</span>
  </div>
);

const Keywords = ({ className = "" }) => (
  <p className={`font-bold tracking-[0.12em] text-white ${className}`}>
    CONFIDENCE <span className="text-teal">•&nbsp;LEADERSHIP&nbsp;•</span> BELONGING
  </p>
);

// teal brushstroke sweep for the base of the brand panel
const Brush = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 1200 300" preserveAspectRatio="none" aria-hidden>
    <path d="M0 190 C 220 120 380 250 620 180 C 840 118 1000 240 1200 165 L1200 300 L0 300 Z" fill="#1E8E8C" opacity="0.55" />
    <path d="M0 220 C 240 160 420 275 660 205 C 880 145 1040 255 1200 195 L1200 300 L0 300 Z" fill="#2FB7B5" opacity="0.85" />
  </svg>
);

export default function Login() {
  const { user, dancerLogin, emailLogin, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setModeRaw] = useState("main"); // main | email | help | forgot | sent | dancer
  const [prevMode, setPrevMode] = useState("main");
  const setMode = (m) => { if (m === "forgot") setPrevMode((p) => (mode === "sent" ? p : mode)); setModeRaw(m); };
  const [loginFailed, setLoginFailed] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "parent", username: "", pin: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (user) navigate(takeReturn()); }, [user, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const wrap = (fn, okMsg) => async (e) => {
    if (e) e.preventDefault();
    try { const u = await fn(); if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(u) : okMsg); navigate(takeReturn()); }
    catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try { const u = await emailLogin(f.email.trim(), f.password); toast.success(`Welcome back, ${u.name}!`); navigate(takeReturn()); }
    catch (err) { setLoginFailed(true); toast.error(err.response?.data?.detail || "Something went wrong"); }
  };
  const handleDancer = wrap(() => dancerLogin(f.username.trim(), f.pin.trim()), (u) => `Let's dance, ${u.name}!`);
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!f.email.includes("@")) { toast.error("Enter the email address the studio has on file"); return; }
    try {
      await forgotPassword(f.email.trim());
      setMode("sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); }
  };

  const Back = ({ to = "main", label = "Back" }) => (
    <button data-testid="login-back" onClick={() => setMode(to)} className="flex items-center gap-1.5 text-sm font-semibold text-teal-dark hover:underline mb-5">
      <ArrowLeft className="w-4 h-4" />{label}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-[1.05fr_1fr] bg-white">
      {/* ---------- Brand panel — black spotlight (top hero on mobile, left column on desktop) ---------- */}
      <div data-testid="login-brand-panel" className="relative flex flex-col items-center lg:items-start justify-start overflow-hidden bg-black text-white px-8 lg:px-20 pt-12 lg:pt-40 pb-40 lg:pb-0">
        {/* sparkles */}
        <Sparkle className="absolute top-8 left-8 lg:top-14 lg:left-12 w-10 h-10 lg:w-16 lg:h-16 text-teal drop-shadow-[0_0_14px_rgba(47,183,181,0.6)]" />
        <Sparkle className="absolute top-5 left-20 lg:top-9 lg:left-28 w-5 h-5 lg:w-7 lg:h-7 text-teal/80" />
        <Sparkle className="absolute top-16 left-16 lg:top-28 lg:left-24 w-3.5 h-3.5 lg:w-5 lg:h-5 text-teal/70" />

        {/* brand text */}
        <div className="relative z-20 max-w-xl text-center lg:text-left">
          <Wordmark className="justify-center lg:justify-start text-5xl lg:text-7xl" />
          <h1 className="mt-5 lg:mt-8 font-head font-bold text-3xl lg:text-5xl text-white leading-tight">Where Confidence</h1>
          <p className="font-script text-teal text-5xl lg:text-7xl leading-none mt-1">Takes Center Stage</p>
          <Keywords className="mt-5 lg:mt-8 text-xs lg:text-sm" />
          <p className="hidden lg:block mt-5 text-white/60 text-base max-w-md leading-relaxed">
            One studio home for directors, teachers, parents and dancers — progress, practice and pride in every step.
          </p>
        </div>

        {/* dancer under a teal spotlight — mobile (centered) */}
        <div className="lg:hidden relative z-10 mt-6 w-52">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-teal/30 blur-3xl" />
          <img src={DANCER} alt="Studio V dancer" className="relative w-full object-contain drop-shadow-2xl" />
        </div>

        {/* dancer under a teal spotlight — desktop (bottom-right) */}
        <div className="hidden lg:block absolute right-8 xl:right-14 bottom-0 z-10 w-72 xl:w-[22rem]">
          <div className="absolute left-1/2 bottom-10 -translate-x-1/2 w-[26rem] h-[26rem] rounded-full bg-teal/25 blur-[90px]" />
          <img src={DANCER} alt="Studio V dancer" className="relative w-full object-contain drop-shadow-2xl" />
        </div>

        {/* brushstroke base */}
        <Brush className="absolute -bottom-1 -left-8 w-[130%] h-32 lg:h-52 z-0" />
      </div>

      {/* ---------- Sign-in panel ---------- */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          {mode === "main" && (
            <>
              <h2 className="font-head text-3xl font-bold tracking-tight">Welcome back</h2>
              <p className="text-muted-foreground mt-1.5">Choose how you'd like to sign in.</p>

              <div className="mt-7 space-y-3">
                <button data-testid="google-login-button" onClick={handleGoogle}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-full py-3.5 font-semibold hover:shadow-md hover:border-gray-300 transition-all duration-200">
                  <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" />Continue with Google
                </button>
                <button data-testid="email-signin-button" onClick={() => setMode("email")}
                  className="w-full flex items-center justify-center gap-3 bg-teal text-white rounded-full py-3.5 font-semibold hover:bg-teal-dark transition-colors duration-200">
                  <Mail className="w-5 h-5" />Sign in with Email
                </button>
                <button data-testid="dancer-signin-toggle" onClick={() => setMode("dancer")}
                  className="w-full flex items-center justify-center gap-2 bg-black text-white rounded-full py-3.5 font-semibold hover:bg-gray-800 transition-colors duration-200">
                  <KeyRound className="w-5 h-5 text-teal" />Dancer Login
                </button>
              </div>
              <p className="mt-8 text-xs text-muted-foreground text-center tracking-[0.12em] font-semibold">
                CONFIDENCE <span className="text-teal">• LEADERSHIP •</span> BELONGING
              </p>
              <div className="mt-6 text-center space-y-2">
                <button data-testid="main-forgot-link" onClick={() => setMode("forgot")} className="block w-full text-sm font-semibold text-teal-dark hover:underline">Forgot your password?</button>
                <button data-testid="need-help-link" onClick={() => setMode("help")} className="text-xs text-muted-foreground hover:text-gray-800 underline underline-offset-2">Need help accessing your account?</button>
              </div>
            </>
          )}

          {mode === "help" && (
            <>
              <Back />
              <h2 className="font-head text-3xl font-bold tracking-tight">Need help signing in?</h2>
              <p className="text-muted-foreground mt-1.5">Your Studio V Hub account is already set up — you don't need to create a new one.</p>
              <ol className="mt-6 space-y-3 text-sm text-gray-800" data-testid="help-tips">
                <li className="rounded-2xl border border-gray-200 p-4"><span className="font-semibold">Forgot or never set a password?</span> Tap <button type="button" data-testid="help-goto-forgot" onClick={() => setMode("forgot")} className="font-semibold text-teal-dark underline">Reset my password</button>, enter the email the studio has on file, and follow the link we email you. It works with Gmail, Yahoo, Outlook, Hotmail, iCloud and any other email.</li>
                <li className="rounded-2xl border border-gray-200 p-4"><span className="font-semibold">Use Gmail?</span> You can also tap <span className="font-semibold">Continue with Google</span> if your Gmail address is the one registered with the studio.</li>
                <li className="rounded-2xl border border-gray-200 p-4"><span className="font-semibold">No email arriving?</span> Wait a couple of minutes and check your Spam, Junk or Promotions folder for a message from Studio V.</li>
                <li className="rounded-2xl border border-teal/30 bg-teal-light/40 p-4"><span className="font-semibold">Still stuck?</span> Email <a href="mailto:kalee@dancestudiov.com" data-testid="help-contact-email" className="font-semibold text-teal-dark underline">kalee@dancestudiov.com</a> and we'll help you get in.</li>
              </ol>
            </>
          )}

          {mode === "sent" && (
            <>
              <Back to="email" label="Back to sign in" />
              <h2 className="font-head text-3xl font-bold tracking-tight" data-testid="reset-sent-title">Check your email</h2>
              <p className="text-muted-foreground mt-1.5">If <span className="font-semibold text-gray-800">{f.email.trim()}</span> is on file at Studio V, we've sent a secure link to set a new password. It works once and expires in 2 hours.</p>
              <ul className="mt-6 space-y-2 text-sm text-gray-700 list-disc pl-5">
                <li>Look for an email from <span className="font-semibold">Studio V</span> — check Spam, Junk or Promotions if you don't see it.</li>
                <li>Open the link on this device, choose your new password, then sign in.</li>
                <li>Didn't get it after a few minutes? <button type="button" data-testid="reset-send-again" onClick={() => setMode("forgot")} className="font-semibold text-teal-dark underline">Send it again</button> or <button type="button" onClick={() => setMode("help")} className="font-semibold text-teal-dark underline">get help</button>.</li>
              </ul>
            </>
          )}

          {mode === "email" && (
            <>
              <Back />
              <h2 className="font-head text-3xl font-bold tracking-tight">Sign in with email</h2>
              <p className="text-muted-foreground mt-1.5">For parents, teachers and admins.</p>
              <form onSubmit={handleEmailLogin} className="mt-7 space-y-4">
                <div><label className="text-sm font-semibold">Email</label><Input data-testid="email-login-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-xl bg-white" placeholder="you@email.com" /></div>
                <div><div className="flex items-center justify-between"><label className="text-sm font-semibold">Password</label><button type="button" data-testid="go-forgot" onClick={() => setMode("forgot")} className="text-sm font-semibold text-teal-dark hover:underline">Forgot password?</button></div><Input data-testid="email-login-password" type="password" autoComplete="current-password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 rounded-xl bg-white" placeholder="••••••••" /></div>
                {loginFailed && <div data-testid="login-failed-hint" className="rounded-xl bg-teal-light/50 border border-teal/30 p-3 text-sm text-gray-800">Not sure of your password, or never set one? <button type="button" onClick={() => setMode("forgot")} className="font-semibold text-teal-dark underline">Reset it by email</button> — it only takes a minute.</div>}
                <button type="submit" data-testid="email-login-submit" className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-3.5 font-semibold transition-colors duration-200">Sign In</button>
              </form>
              <p className="mt-4 text-xs text-muted-foreground text-center">You'll stay signed in on this device for 30 days. <button type="button" data-testid="go-help" onClick={() => setMode("help")} className="underline">Need help?</button></p>
            </>
          )}

          {mode === "forgot" && (
            <>
              <Back to={prevMode === "forgot" ? "main" : prevMode} />
              <h2 className="font-head text-3xl font-bold tracking-tight">Reset your password</h2>
              <p className="text-muted-foreground mt-1.5">Enter the email address the studio has on file. We'll email you a secure link to choose a new password — this also works if you've never set one.</p>
              <form onSubmit={handleForgot} className="mt-7 space-y-4">
                <div><label className="text-sm font-semibold">Email</label><Input data-testid="forgot-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-xl bg-white" placeholder="you@email.com" /></div>
                <button type="submit" data-testid="forgot-submit" className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-3.5 font-semibold transition-colors duration-200">Send reset link</button>
              </form>
            </>
          )}

          {mode === "dancer" && (
            <>
              <Back />
              <h2 className="font-head text-3xl font-bold tracking-tight">Dancer sign in</h2>
              <p className="text-muted-foreground mt-1.5">Use your username and PIN — no email needed.</p>
              <form onSubmit={handleDancer} className="mt-7 space-y-4">
                <div><label className="text-sm font-semibold">Username</label><Input data-testid="dancer-username-input" value={f.username} onChange={(e) => set("username", e.target.value)} className="mt-1.5 rounded-xl bg-white" placeholder="e.g. emma" autoFocus /></div>
                <div><label className="text-sm font-semibold">PIN</label><Input data-testid="dancer-pin-input" type="password" inputMode="numeric" value={f.pin} onChange={(e) => set("pin", e.target.value)} className="mt-1.5 rounded-xl bg-white" placeholder="••••" /></div>
                <button type="submit" data-testid="dancer-login-submit" className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-3.5 font-semibold transition-colors duration-200">Let's Dance</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
